import type { PortfolioOrder } from "../types/portfolio";

export interface ChatOrderPromotionAttachment {
  kind: "order_promo";
  orderId: number | null;
  teamCode: string;
  teamName: string;
  side: "BUY" | "SELL";
  price: number;
  quantity: number;
  teamPrimaryColor: string | null;
  teamSecondaryColor: string | null;
}

export interface ChatComposerDraft {
  id: string;
  text: string;
  attachment: ChatOrderPromotionAttachment | null;
}

export function getPromotionTargetSide(side: ChatOrderPromotionAttachment["side"]): ChatOrderPromotionAttachment["side"] {
  return side === "BUY" ? "SELL" : "BUY";
}

function isNullableString(value: unknown) {
  return value === null || typeof value === "string";
}

function isNullableNumber(value: unknown) {
  return value === null || typeof value === "number";
}

function roundQuantity(quantity: number) {
  return Math.round(quantity * 100) / 100;
}

export function getPromotedOrderQuantity(
  order: Pick<PortfolioOrder, "open_quantity" | "original_quantity" | "filled_quantity">,
): number {
  const remaining = roundQuantity(order.open_quantity);
  if (Number.isFinite(remaining) && remaining > 0) {
    return remaining;
  }

  const derivedRemaining = roundQuantity(order.original_quantity - order.filled_quantity);
  if (Number.isFinite(derivedRemaining) && derivedRemaining > 0) {
    return derivedRemaining;
  }

  return Math.max(1, roundQuantity(order.original_quantity));
}

export function buildOrderPromotionAttachment(order: PortfolioOrder): ChatOrderPromotionAttachment {
  return {
    kind: "order_promo",
    orderId: order.trade_order_id,
    teamCode: order.team_code,
    teamName: order.display_team_name ?? order.team_name ?? order.team_code,
    side: order.side,
    price: order.price,
    quantity: getPromotedOrderQuantity(order),
    teamPrimaryColor: order.team_primary_color,
    teamSecondaryColor: order.team_secondary_color,
  };
}

export function getValidOrderPromotionAttachment(attachment: unknown): ChatOrderPromotionAttachment | null {
  if (!attachment || typeof attachment !== "object") {
    return null;
  }

  const candidate = attachment as Record<string, unknown>;
  if (candidate.kind !== "order_promo") {
    return null;
  }

  if (
    typeof candidate.teamCode !== "string" ||
    candidate.teamCode.trim().length === 0 ||
    typeof candidate.teamName !== "string" ||
    candidate.teamName.trim().length === 0 ||
    (candidate.side !== "BUY" && candidate.side !== "SELL") ||
    typeof candidate.price !== "number" ||
    !Number.isFinite(candidate.price) ||
    typeof candidate.quantity !== "number" ||
    !Number.isFinite(candidate.quantity) ||
    !isNullableNumber(candidate.orderId) ||
    !isNullableString(candidate.teamPrimaryColor) ||
    !isNullableString(candidate.teamSecondaryColor)
  ) {
    return null;
  }

  return {
    kind: "order_promo",
    orderId: candidate.orderId,
    teamCode: candidate.teamCode.trim().toUpperCase(),
    teamName: candidate.teamName.trim(),
    side: candidate.side,
    price: candidate.price,
    quantity: candidate.quantity,
    teamPrimaryColor: candidate.teamPrimaryColor,
    teamSecondaryColor: candidate.teamSecondaryColor,
  };
}

function buildPromotionDraftText(attachment: ChatOrderPromotionAttachment): string {
  const action = attachment.side === "BUY" ? "Buying" : "Selling";
  return `${action} ${attachment.quantity} ${attachment.teamCode} @ ${attachment.price}.`;
}

export function buildWebOrderPromotionDraft(order: PortfolioOrder): ChatComposerDraft {
  const attachment = buildOrderPromotionAttachment(order);

  return {
    id: `${attachment.orderId ?? attachment.teamCode}-${Date.now()}`,
    text: buildPromotionDraftText(attachment),
    attachment,
  };
}
